import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTestimonials } from "@/lib/content";

export default function TestimonialCarousel() {
  const { data: testimonials } = useTestimonials();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!testimonials || testimonials.length === 0) return;
    const id = setInterval(() => {
      setIndex((p) => (p + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(id);
  }, [testimonials]);

  if (!testimonials || testimonials.length === 0) return null;
  const t = testimonials[Math.min(index, testimonials.length - 1)];

  return (
    <section className="bg-[#fafafa] py-20 md:py-28" data-testid="testimonials-carousel">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-6">
          Client Voice
        </div>
        <div className="text-[#ffde59] mb-6 flex justify-center">
          <i className="fa-solid fa-quote-left text-4xl" aria-hidden="true" />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-10">
              {t.title && (
                <p className="font-serif font-bold text-xl sm:text-2xl lg:text-3xl text-[#0a2540] leading-snug mb-4">
                  {t.title}
                </p>
              )}
              <p className="font-serif text-xl sm:text-2xl lg:text-3xl text-[#0a2540] leading-snug italic">
                "{t.quote}"
              </p>
            </div>
            <div className="flex items-center justify-center gap-4">
              <img
                src={t.avatar}
                alt={t.name}
                loading="lazy"
                className="h-12 w-12 rounded-full object-cover ring-2 ring-[#ffde59]/60"
              />
              <div className="text-left">
                <div className="font-serif text-[#0a2540] text-lg">{t.name}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{t.role}</div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-center gap-2 mt-12">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Show testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
              className={`h-1 rounded-full transition-all ${
                i === index ? "w-10 bg-[#0a2540]" : "w-4 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
